import { MissionBrand } from "@/components/mission-chrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { redeemProjectInvite, type RedeemInviteResult } from "@/lib/api";
import { FormEvent, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

export function InvitePage() {
  const [params] = useSearchParams();
  const preset = useMemo(() => (params.get("token") || "").trim(), [params]);
  const [token, setToken] = useState(preset);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<RedeemInviteResult | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const body = await redeemProjectInvite(token.trim());
      setResult(body);
    } catch (e) {
      setResult(null);
      setErr(e instanceof Error ? e.message : "redeem failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mission-surface relative h-full min-h-0 overflow-y-auto overflow-x-hidden bg-black text-[#f0f0fa]">
      <header className="sticky top-0 z-20 flex h-[72px] items-center gap-6 border-b border-white/10 bg-black/80 px-6 backdrop-blur-md sm:px-10">
        <Link to="/" className="no-underline">
          <MissionBrand />
        </Link>
        <div className="flex-1" />
        <Link to="/login" className="text-[12px] tracking-[0.08em] text-white/72 uppercase no-underline hover:text-white">
          Sign in
        </Link>
      </header>
      <main className="mx-auto max-w-[520px] px-6 py-10 sm:px-10 sm:py-14">
        <p className="mb-3 text-[11px] font-semibold tracking-[0.16em] text-white/50 uppercase">
          pcbx.ai · PlatformACL
        </p>
        <h1 className="m-0 text-[32px] leading-[1.1] font-bold tracking-[-0.02em] text-white">Redeem invite</h1>
        <p className="mt-3 text-[15px] leading-[1.6] text-white/78">
          Sign in with Better Auth, then redeem a project invite. The server hashes the token and writes{" "}
          <span className="font-mono text-[13px]">project_members</span> as editor or viewer. Non-members stay 403.
        </p>
        <form className="mt-8 flex flex-col gap-3" onSubmit={(event) => void onSubmit(event)}>
          <Label htmlFor="invite-token">Invite token</Label>
          <Input
            id="invite-token"
            name="token"
            autoComplete="off"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="pcbx_inv_…"
            className="font-mono"
          />
          <Button type="submit" disabled={busy || !token.trim()}>
            {busy ? "Redeeming…" : "Redeem"}
          </Button>
        </form>
        {err && (
          <p className="mt-4 text-[13px] text-red-300" role="alert">
            {err}
          </p>
        )}
        {result && (
          <p className="mt-4 text-[13px] text-white/80" data-testid="invite-redeemed">
            Joined {result.project_id} as {result.member_role}
            {result.already_member ? " (already a member)" : ""}.
          </p>
        )}
      </main>
    </div>
  );
}
